import { type FC, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Drawer } from "antd";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import CustomChartTooltip from "src/components/reusable/CustomChartTooltip/CustomChartTooltip";
import { DashboardErrorState } from "src/components/common";
import type {
  PaidMediaOverviewCampaignDetailData,
  PaidMediaOverviewCampaignRow,
  PaidMediaOverviewPlatform,
  PaidMediaOverviewScalePoint,
} from "src/services/PaidMediaOverviewAPI";
import {
  formatOverviewAmount,
  formatOverviewCurrency,
  formatOverviewDateLabel,
  formatOverviewMultiplier,
  formatOverviewNumber,
  formatOverviewPct,
  formatOverviewText,
  formatOverviewUnitCost,
} from "../../utils/paidMediaOverviewFormat";
import { overviewPlatformLabel } from "../../utils/paidMediaOverviewLabels";
import { overviewPlatformSlug } from "../../data/paidMediaOverviewPlatforms";
import { OverviewChartEmptyState, OverviewStatusBadge } from "./overviewPrimitives";
import {
  OVERVIEW_BENCHMARK_COLOR,
  OVERVIEW_GRID_COLOR,
  OVERVIEW_PLATFORM_COLOR,
  formatOverviewShortDate,
  overviewAxisProps,
} from "./overviewChartConfig";
import { usePaidMediaChartRtl } from "../../utils/paidMediaChartRtl";

const DRAWER_ROOT_CLASS = "paid-media-overview__campaign-drawer";

export interface PaidMediaOverviewCampaignSelection {
  platform: PaidMediaOverviewPlatform;
  campaign_id: string;
  campaign_name: string | null;
  status_code: string | null;
  status_label: string | null;
  status_reason: string | null;
}

export const campaignSelectionFromRow = (
  row: PaidMediaOverviewCampaignRow
): PaidMediaOverviewCampaignSelection => ({
  platform: row.platform,
  campaign_id: row.campaign_id,
  campaign_name: row.campaign_name ?? null,
  status_code: row.status_code ?? null,
  status_label: row.status_label ?? null,
  status_reason: row.status_reason ?? null,
});

/** Scale points carry no status classification — the badge renders as empty. */
export const campaignSelectionFromScalePoint = (
  point: PaidMediaOverviewScalePoint
): PaidMediaOverviewCampaignSelection | null =>
  point.campaign_id
    ? {
        platform: point.platform,
        campaign_id: point.campaign_id,
        campaign_name: point.label ?? null,
        status_code: null,
        status_label: null,
        status_reason: null,
      }
    : null;

interface CampaignDetailDrawerProps {
  selectedRow: PaidMediaOverviewCampaignSelection | null;
  detail: PaidMediaOverviewCampaignDetailData | null;
  currency: string | null;
  loading: boolean;
  error: string | null;
  notFound: boolean;
  onOpenChange: (_open: boolean) => void;
}

const CampaignDetailDrawer: FC<CampaignDetailDrawerProps> = ({
  selectedRow,
  detail,
  currency,
  loading,
  error,
  notFound,
  onOpenChange,
}) => {
  const { t, i18n } = useTranslation();
  const chartRtl = usePaidMediaChartRtl();

  if (!selectedRow) {
    return (
      <Drawer
        open={false}
        onClose={() => onOpenChange(false)}
        width={560}
        placement={chartRtl.isRTL ? "left" : "right"}
        rootClassName={DRAWER_ROOT_CLASS}
      />
    );
  }

  const header = detail?.header ?? null;
  const kpis = detail?.kpis ?? null;
  const platform = header?.platform ?? selectedRow.platform;
  const platformClass = `paid-media-overview__drawer-platform-tag--${overviewPlatformSlug(platform)}`;
  const platformColor = OVERVIEW_PLATFORM_COLOR[platform];

  const trendSeries = (detail?.trend?.series ?? []).map(point => ({
    label: formatOverviewShortDate(point?.date ?? "", i18n.language),
    spend: point?.spend ?? null,
    revenue: point?.revenue ?? null,
  }));

  const renderBody = (): ReactNode => {
    if (loading) {
      return <LoadingSpinner compact />;
    }
    if (notFound) {
      return (
        <p className="paid-media-overview__leaderboard-empty" role="status">
          {t("paidMediaOverview.drawer.notFound")}
        </p>
      );
    }
    if (error) {
      return <DashboardErrorState message={error} />;
    }

    return (
      <>
        <div className="paid-media-overview__drawer-kpi-grid">
          <MiniKpi
            label={t("paidMediaOverview.metrics.spend")}
            value={formatOverviewAmount(kpis?.spend, currency)}
          />
          <MiniKpi
            label={t("paidMediaOverview.metrics.revenue")}
            value={formatOverviewAmount(kpis?.revenue, currency)}
          />
          <MiniKpi
            label={t("paidMediaOverview.metrics.purchases")}
            value={formatOverviewNumber(kpis?.purchases)}
          />
          <MiniKpi
            label={t("paidMediaOverview.metrics.roas")}
            value={formatOverviewMultiplier(kpis?.roas)}
          />
          <MiniKpi
            label={t("paidMediaOverview.metrics.cpa")}
            value={formatOverviewUnitCost(kpis?.cost_per_purchase, currency)}
          />
          <MiniKpi
            label={t("paidMediaOverview.metrics.ctr")}
            value={formatOverviewPct(kpis?.ctr, 2)}
          />
        </div>

        <div className="paid-media-overview__drawer-chart-card">
          <div className="paid-media-overview__drawer-chart-header">
            <span>{t("paidMediaOverview.drawer.chartTitle")}</span>
            <OverviewStatusBadge
              code={selectedRow.status_code ?? null}
              label={selectedRow.status_label ?? null}
              reason={selectedRow.status_reason ?? null}
            />
          </div>
          <div className="paid-media-overview__chart paid-media-overview__chart--sm">
            {trendSeries.length ? (
              <ResponsiveContainer>
                <ComposedChart
                  data={trendSeries}
                  className="paid-media-daily-spend-revenue-trend"
                >
                  <CartesianGrid stroke={OVERVIEW_GRID_COLOR} vertical={false} />
                  <XAxis dataKey="label" reversed={chartRtl.axisReversed} {...overviewAxisProps} />
                  <YAxis
                    yAxisId="l"
                    orientation={chartRtl.yAxisOrientation}
                    tickFormatter={value => formatOverviewCurrency(value, currency)}
                    {...overviewAxisProps}
                  />
                  <YAxis
                    yAxisId="r"
                    orientation={chartRtl.oppositeYAxisOrientation}
                    tickFormatter={value => formatOverviewCurrency(value, currency)}
                    {...overviewAxisProps}
                  />
                  <Tooltip
                    wrapperClassName="paid-media-overview__chart-tooltip-wrapper"
                    content={
                      <CustomChartTooltip
                        formatTooltip={(value: number) => formatOverviewCurrency(value, currency)}
                      />
                    }
                  />
                  <Bar
                    yAxisId="l"
                    dataKey="spend"
                    name={t("paidMediaOverview.metrics.spend")}
                    fill={platformColor}
                    fillOpacity={0.7}
                    radius={[3, 3, 0, 0]}
                  />
                  <Line
                    yAxisId="r"
                    type="monotone"
                    dataKey="revenue"
                    name={t("paidMediaOverview.metrics.revenue")}
                    stroke={OVERVIEW_BENCHMARK_COLOR}
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <OverviewChartEmptyState />
            )}
          </div>
        </div>

        {detail?.insight ? (
          <div className="paid-media-overview__drawer-insight">
            <strong>{t("paidMediaOverview.drawer.insightLabel")}</strong> {detail.insight}
          </div>
        ) : null}
      </>
    );
  };

  return (
    <Drawer
      open
      onClose={() => onOpenChange(false)}
      width={560}
      destroyOnClose
      placement={chartRtl.isRTL ? "left" : "right"}
      rootClassName={DRAWER_ROOT_CLASS}
      title={
        <div className="paid-media-overview__drawer-title">
          <div>{formatOverviewText(header?.campaign_name ?? selectedRow.campaign_name)}</div>
          <div className="paid-media-overview__drawer-subtitle">
            <span className={`paid-media-overview__drawer-platform-tag ${platformClass}`}>
              {overviewPlatformLabel(t, platform)}
            </span>
            <span>{formatOverviewText(header?.objective_or_network)}</span>
            <span aria-hidden>·</span>
            <span>
              {t("paidMediaOverview.drawer.started", {
                date: formatOverviewDateLabel(header?.start_date),
              })}
            </span>
          </div>
        </div>
      }
    >
      <div className="paid-media-overview__drawer-body">
        {renderBody()}

        <div className="paid-media-overview__drawer-actions">
          <button
            type="button"
            className="paid-media-overview__drawer-close"
            onClick={() => onOpenChange(false)}
          >
            {t("paidMediaOverview.drawer.close")}
          </button>
        </div>
      </div>
    </Drawer>
  );
};

const MiniKpi: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="paid-media-overview__mini-kpi">
    <div className="paid-media-overview__mini-kpi-label">{label}</div>
    <div className="paid-media-overview__mini-kpi-value">{value}</div>
  </div>
);

export default CampaignDetailDrawer;
